import {
  AddOutlined,
  ButtonDS,
  EditOutlined,
  LaunchPage,
  NotInterestedOutlined,
  RemoveRedEyeOutlined,
  SidePanelDS,
  Spacer,
  Tag,
  TextCapitalized,
} from "@qivia/ui";
import { t } from "i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import styled from "styled-components";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { listAllVehiclesInDbAsync, selectListAllVehiclesInDb, selectListAllVehiclesInDbStatus } from "../../vehicles/vehiclesSlice";
import { selectDepartments } from "../departmentsSlice";
import { selectCompany } from "../../homeSlice";
import { DepartmentDeleteModal } from "../modals/departmentDeleteModal";
import { AddVehicleToDepartmentModal } from "../modals/addVehicleToDepartmentModal";

export interface DepartmentSidePanelProps {
  canAddVehicle?: boolean;
  canDeactivate?: boolean;
}

export const DepartmentSidePanel = (props: DepartmentSidePanelProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const firstPath = location.pathname.split("/")[1]; 
  const params = useParams();

  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isAddVehicleModalVisible, setIsAddVehicleModalVisible] = useState(false);

  const company = useAppSelector(selectCompany);
  const departments = useAppSelector(selectDepartments);
  const vehicles = useAppSelector(selectListAllVehiclesInDb);
  const listAllVehiclesInDbStatus = useAppSelector(selectListAllVehiclesInDbStatus);

  const department = useMemo(() => {
    if (departments) {
        return departments.find((department) => department.uuid === params.id);
    }
  }, [departments, params.id]);

  const vehiclesByDepartment = useMemo(() => {
    return vehicles.filter((vehicle) => vehicle.department === params.id);
  }, [params.id, vehicles]);

  useEffect(() => {
    if (!department) {
      navigate(`/${firstPath}/departments`);
    }
  }, [navigate, department, firstPath, vehiclesByDepartment]);

  useEffect(() => {
    if (company && listAllVehiclesInDbStatus === "idle") {
      void dispatch(listAllVehiclesInDbAsync({ companyUuid: company.uuid }));
    }
  }, [company, dispatch, listAllVehiclesInDbStatus]);

  const onSidePanelClosed = useCallback(() => {
    navigate(`/${firstPath}/departments`);
  }, [firstPath, navigate]);

  const onEditDepartment = useCallback(() => {
    console.log("modification du département");
  }, []);

  if (!department) {
    return;
  }

  if (!vehiclesByDepartment) return <LaunchPage hasBorderRadius={true} />;

  return (
    <SidePanelDS.SidePanel
      visible={true}
      onClose={onSidePanelClosed}
      absoluteSection={
        <>
          {props.canAddVehicle && (
            <>
              <ButtonDS
                leftIcon={<AddOutlined />}
                text={t("departments.sidepanel.addVehicle")}
                format={"fill"}
                buttonType={"primary"}
                onClick={() => setIsAddVehicleModalVisible(true)}
                color={"primary"}
              />
              <Spacer y={1} />
            </>
          )}
          {props.canDeactivate && (
            <ButtonDS
              leftIcon={<NotInterestedOutlined />}
              text={t("departments.sidepanel.delete")}
              format={"fill"}
              buttonType={"secondary"}
              onClick={() => setIsDeleteModalVisible(true)}
              iconColor={"errorNormal"}
            />
          )}
        </>
      }
    >
      <SidePanelDS.Section verticalSpacing={1.2} horizontalSpacing={1.5}>
        <StyledPanelContainer>
          <StyledContent>{department.name}</StyledContent>
        </StyledPanelContainer>
      </SidePanelDS.Section>

      <SidePanelDS.Section verticalSpacing={1.5} horizontalSpacing={1.5}>
        <StyledSectionTitle>
          <TextCapitalized>
            {t("departments.sidepanel.associatedVehicles")}
          </TextCapitalized>
          <ButtonDS 
            singleIcon={{ icon: <EditOutlined />, size: "S" }} 
            format="fill" 
            buttonType="tertiary"
            onClick={onEditDepartment}
          />
        </StyledSectionTitle>
        <Spacer y={2} />
        {vehiclesByDepartment.length > 0 ? (
          <StyledVehicleList>
            {vehiclesByDepartment.map((vehicle) => (
                <StyledVehicleItem key={vehicle.uuid}>
                    <SidePanelDS.SectionedActionStripe
                    left={
                      <StyledRowCenter>
                        <Spacer x={0.5} />
                        <StyledVehicleResume>{vehicle.registrationNumber}</StyledVehicleResume>
                        <Spacer x={1} />
                        {vehicle.reference && (
                            <>
                              <Tag
                                textColor={colors["colors/text/darkGrey"]}
                                text={vehicle.reference}
                                backgroundColor={
                                colors["colors/surfaces/background/background_level0"]
                                }
                                borderColor={colors["colors/text/darkGrey"]}
                              />
                              <Spacer x={0.5} />
                            </>
                        )}
                        </StyledRowCenter>
                    }
                    right={
                        <StyledLittleIcon>
                        <RemoveRedEyeOutlined
                            key="vehicle"
                            onClick={() =>
                            navigate(`/home/vehicles/${vehicle.uuid}`)
                            }
                        />
                        <Spacer x={0.5} />
                        </StyledLittleIcon>
                    }
                    />
                </StyledVehicleItem>
            ))}
        </StyledVehicleList>
        ) : (
          <StyledRowCenter>
            <StyledEmptyVehiclesList>
            <TextCapitalized>
              {t("departments.sidepanel.empty")}
            </TextCapitalized>
              </StyledEmptyVehiclesList>
          </StyledRowCenter>
        )}
      </SidePanelDS.Section>
      <AddVehicleToDepartmentModal
        visible={isAddVehicleModalVisible}
        departmentUuid={department.uuid}
        onCloseModal={() => {
          setIsAddVehicleModalVisible(false);
        }}
      />
      <DepartmentDeleteModal 
        isVisible={isDeleteModalVisible} 
        departmentUuid={department.uuid} 
        onCloseModal={() => setIsDeleteModalVisible(false)}
      />
    </SidePanelDS.SidePanel>
  );
};

const StyledSectionTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  & button svg {
    width: 1.25rem;
    height: 1.25rem;
  };
`;

const StyledPanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const StyledContent = styled.div`
  ${typographies["Header/H4"]};
`;

const StyledRowCenter = styled.div`
  ${typographies["Body/S"]}
  display: flex;
  align-items: center;
  & svg {
    width: 1rem;
    height: 1rem;
  };
  white-space: nowrap;
`;
const StyledLittleIcon = styled.div`
  display: flex;
  cursor: pointer;
  & svg {
    width: 1rem;
    height: 1rem;
  }
`;

const StyledVehicleResume = styled.span`
  ${typographies["Body/XS"]};
  color: ${colors["colors/text/black"]};
  text-align: center;
`;

const StyledVehicleList = styled.div`
  border: .025rem solid ${colors["colors/text/veryLightGrey"]};
  border-radius: 1rem;
  overflow: hidden;
`;

const StyledVehicleItem = styled.div`
  &:not(:first-child) {
    border-top: .025rem solid ${colors["colors/text/veryLightGrey"]};
  }
`;

const StyledEmptyVehiclesList = styled.div`
  ${typographies["Body/XS"]}
  color: ${colors["colors/text/darkGrey"]};
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;